import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  BatteryCharging,
  Brain,
  MapPin,
  ChevronLeft,
  Home as HomeIcon,
  Signal,
  Wifi,
  BatteryFull,
  Menu,
  X,
  Check,
  Gauge,
  RefreshCw,
  Lock,
  Dna,
  TrendingUp,
  Wrench,
  BookOpen,
} from "lucide-react";
import { Logo } from "../components/Logo";
import { VoteNudge } from "../components/VoteNudge";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { MobileAppInstallBanner } from "../components/app/MobileAppInstallBanner";
import { OwnerModeBanner } from "../components/app/OwnerModeBanner";
import { FounderChip } from "../components/app/FounderChip";
import { stateFromCoords } from "../lib/geoLookup";
import { toast } from "sonner";
import { VehicleProvider, useVehicle } from "../lib/vehicleContext";
import { MODEL_LIST } from "../lib/vehicles";
import { US_STATES } from "../lib/climateData";
import {
  useSubscription,
  openSubscribeGate,
  ensurePreviewStarted,
  activateFoundingMember,
  activateFoundingByEmail,
} from "../lib/subscription";
// Tab views live in their own files after the P1 refactor. Keeping them
// out of this shell file drops it from ~3200 lines to ~800 and lets each
// view be reasoned about (and hot-reloaded) independently.
import { HomeView } from "../components/app/views/HomeView";
import { AIView } from "../components/app/views/AIView";
import { RangeView } from "../components/app/views/RangeView";
import { TripView } from "../components/app/views/TripView";
import { MoreView } from "../components/app/views/MoreView";
import { CareView } from "../components/app/views/CareView";
import { BatteryDNAView } from "../components/app/views/BatteryDNAView";
import { PredictView } from "../components/app/views/PredictView";
import { LiveGate } from "../components/app/views/LiveGate";
import { BlogView, BLOG_LAST_READ_KEY } from "../components/app/views/BlogView";
import { PreviewExpiredLock } from "../components/app/PreviewExpiredLock";
import { FoundingMemberBanner } from "../components/app/FoundingMemberBanner";
import { HomePriceOnboarding } from "../components/app/HomePriceOnboarding";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

// Per-vehicle usage overrides — matches the marketing site Intelligence section
const VEHICLE_USAGE_HINTS = {
  "model-3-lr":       { supercharger_pct: 28, parked_at_100pct_days_per_month: 3, preconditions_in_winter: true },
  "model-s-lr":       { supercharger_pct: 22, parked_at_100pct_days_per_month: 1, preconditions_in_winter: true },
  "model-s-plaid-2021": { supercharger_pct: 42, parked_at_100pct_days_per_month: 5, preconditions_in_winter: true },
  "model-y-lr":       { supercharger_pct: 30, parked_at_100pct_days_per_month: 2, preconditions_in_winter: true },
  "model-x-plaid":    { supercharger_pct: 38, parked_at_100pct_days_per_month: 4, preconditions_in_winter: true },
  "cybertruck":       { supercharger_pct: 48, parked_at_100pct_days_per_month: 6, preconditions_in_winter: false },
  "roadster-2026":    { supercharger_pct: 55, parked_at_100pct_days_per_month: 4, preconditions_in_winter: true },
};

const pingSync = () => {
  try {
    fetch(`${API_BASE}/api/stats/sync-ping`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ source: "mobile-app" }),
    });
  } catch (_e) {
    // best-effort telemetry
  }
};

const recordWarrantyClaim = (soh) => {
  try {
    fetch(`${API_BASE}/api/stats/warranty-claim`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ soh_pct: soh }),
    });
  } catch (_e) {
    // best-effort telemetry
  }
};

const TAB_DEFS = [
  { id: "home",    tKey: "app.tab_home",    fallback: "Home",       icon: HomeIcon },
  { id: "dna",     tKey: "app.tab_dna",     fallback: "Health Profile", icon: Dna },
  { id: "ai",      tKey: "app.tab_ai",      fallback: "AI",         icon: Brain },
  { id: "predict", tKey: "app.tab_predict", fallback: "Predict",    icon: TrendingUp },
  { id: "care",    tKey: "app.tab_care",    fallback: "Warranty",   icon: Wrench },
  { id: "blog",    tKey: "app.tab_blog",    fallback: "Blog",       icon: BookOpen },
];

export default function MobileApp() {
  return (
    <VehicleProvider>
      <MobileAppShell />
    </VehicleProvider>
  );
}

function MobileAppShell() {
  const [tab, setTab] = useState("home");
  const [isDesktop, setIsDesktop] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [stateCode, setStateCode] = useState("CA");
  // Sub-view inside the "More" tab. Lifted up so tapping the More tab in the
  // bottom tab bar resets us to the More hub (no need for a Back button).
  const [moreSub, setMoreSub] = useState(null);
  const { paid, preview, hasPremium } = useSubscription();

  // New-article badge: dot on the Blog tab when a post published after the
  // owner's last read. Cleared the moment they open the tab.
  const [blogNew, setBlogNew] = useState(false);
  const latestPostRef = useRef(null);
  useEffect(() => {
    fetch(`${API_BASE}/api/blog?limit=1`)
      .then((r) => (r.ok ? r.json() : { rows: [] }))
      .then((d) => {
        const latest = d.rows?.[0]?.published_at || null;
        latestPostRef.current = latest;
        let lastRead = null;
        try { lastRead = window.localStorage.getItem(BLOG_LAST_READ_KEY); } catch { /* noop */ }
        if (latest && latest !== lastRead) setBlogNew(true);
      })
      .catch(() => {});
  }, []);

  // Hard-gate: once the 7-day free preview is up and the visitor hasn't
  // subscribed (and isn't in owner-mode), block all app content behind a
  // full-frame lock overlay. Owner-mode / paid tiers keep hasPremium true.
  const showExpiredLock = !hasPremium && preview.expired && !paid;

  useEffect(() => {
    // Start the 7-day free preview countdown the first time this
    // visitor loads the app. If they've already got a paid tier, this
    // no-ops. Preview state is read via useSubscription() throughout /app.
    ensurePreviewStarted();
  }, []);

  useEffect(() => {
    // Legacy QR fallback: already-printed cards encode eviq.pro/app (no /scan
    // hop). Count a device's very FIRST direct landing here as a card scan —
    // referrer is empty for camera-app opens, and the flag stops repeats.
    try {
      const seen = localStorage.getItem("eviq.device_seen");
      const standalone = window.matchMedia("(display-mode: standalone)").matches;
      if (!seen && !standalone && document.referrer === "" && !sessionStorage.getItem("eviq.scan_counted")) {
        sessionStorage.setItem("eviq.scan_counted", "1");
        fetch(`${API_BASE}/api/scan/track?source=app_qr_legacy`, {
          method: "POST", keepalive: true,
        }).catch(() => {});
      }
      localStorage.setItem("eviq.device_seen", "1");
    } catch { /* noop */ }
  }, []);

  useEffect(() => {
    // Permanent owner unlock via URL — for the app owner/admin. Visiting
    // /app?owner_unlock=EVIQ_OWNER_STURGIS_2026 sets a persistent flag that
    // (a) enables owner-mode (paywall bypass), and
    // (b) pre-populates the welcome name so the Tesla auto-detect isn't
    //     needed. The URL is a shared secret only shared with the owner.
    // Once bookmarked, subsequent visits just work — the flag lives forever
    // in localStorage until manually exited via the OwnerModeBanner button.
    try {
      const p = new URLSearchParams(window.location.search);
      const key = p.get("owner_unlock");
      if (key === "EVIQ_OWNER_STURGIS_2026") {
        localStorage.setItem("eviq.owner_mode", "1");
        localStorage.setItem("eviq.tesla_user_name", "Sturgis Griffin");
        window.dispatchEvent(new CustomEvent("eviq:subscription-changed"));
        p.delete("owner_unlock");
        const clean = window.location.pathname + (p.toString() ? "?" + p.toString() : "");
        window.history.replaceState({}, "", clean);
      }

      // Owner Ford demo — /app?ford_demo=EVIQ_OWNER_STURGIS_2026 (or
      // ford_demo=1 when owner mode is already unlocked) creates a mock
      // Ford session so the owner can review Ford screens without a
      // FordPass account. Exit via the normal Disconnect button.
      const fordDemo = p.get("ford_demo");
      if (fordDemo && (fordDemo === "EVIQ_OWNER_STURGIS_2026" || localStorage.getItem("eviq.owner_mode") === "1")) {
        p.delete("ford_demo");
        fetch(`${API_BASE}/api/oauth/ford/demo`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ code: "EVIQ_OWNER_STURGIS_2026" }),
        })
          .then((r) => (r.ok ? r.json() : Promise.reject()))
          .then((j) => {
            localStorage.setItem("eviq.tesla_session_id", j.session_id);
            window.location.replace(window.location.pathname + (p.toString() ? "?" + p.toString() : ""));
          })
          .catch(() => toast.error("Ford demo unavailable — try again"));
      }

      // Owner Volvo demo — /app?volvo_demo=… mirrors the Ford demo:
      // a mock Volvo EX40 session, exit via the Disconnect button.
      const volvoDemo = p.get("volvo_demo");
      if (volvoDemo && (volvoDemo === "EVIQ_OWNER_STURGIS_2026" || localStorage.getItem("eviq.owner_mode") === "1")) {
        p.delete("volvo_demo");
        fetch(`${API_BASE}/api/oauth/volvo/demo`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ code: "EVIQ_OWNER_STURGIS_2026" }),
        })
          .then((r) => (r.ok ? r.json() : Promise.reject()))
          .then((j) => {
            localStorage.setItem("eviq.tesla_session_id", j.session_id);
            window.location.replace(window.location.pathname + (p.toString() ? "?" + p.toString() : ""));
          })
          .catch(() => toast.error("Volvo demo unavailable — try again"));
      }

      // Referral capture — a friend arriving via /app?ref=CODE gets the
      // code stored locally; it's attached to their Stripe checkout so the
      // referrer earns their free-month credit automatically.
      const refCode = p.get("ref");
      if (refCode && /^[a-z0-9]{4,12}$/i.test(refCode)) {
        try { localStorage.setItem("eviq.referral_code", refCode.toUpperCase()); } catch { /* noop */ }
        toast.success("Referral link applied", {
          description: "Your friend gets credit when you subscribe.",
        });
        p.delete("ref");
        const cleanRef = window.location.pathname + (p.toString() ? "?" + p.toString() : "");
        window.history.replaceState({}, "", cleanRef);
      }

      // Founding-member unlock via URL — hand-picked early supporters get
      // lifetime-tier access via `/app?founding=<code>`. The code is matched
      // against the FOUNDING_MEMBERS allowlist in subscription.js; unknown
      // codes are ignored silently. Admin routes are NOT unlocked by this.
      const foundingCode = p.get("founding");
      if (foundingCode) {
        const activated = activateFoundingMember(foundingCode);
        if (activated) {
          toast.success(`Welcome, ${activated.name} · Founding Member ${activated.badge}`, {
            description: "All premium features unlocked. Thank you for being an early supporter.",
            duration: 6000,
          });
        }
        p.delete("founding");
        const clean = window.location.pathname + (p.toString() ? "?" + p.toString() : "");
        window.history.replaceState({}, "", clean);
      }
    } catch (_e) {}
  }, []);

  useEffect(() => {
    // Continuity Flow — desktop admin scans a QR that lands here with
    // ?handoff=<token>. We POST the token to /api/continuity/consume-handoff;
    // if valid (short-lived, one-shot), the server returns the owner name
    // and we flip owner mode ON locally. Zero re-authentication for the
    // app owner across devices.
    const p = new URLSearchParams(window.location.search);
    const token = p.get("handoff");
    if (!token) return;
    // Strip the token from the URL right away — even if the network call
    // fails we don't want the QR URL sitting in the address bar.
    p.delete("handoff");
    const clean = window.location.pathname + (p.toString() ? "?" + p.toString() : "");
    window.history.replaceState({}, "", clean);

    // Guard against React StrictMode double-invocation: the token is
    // consumed exactly once server-side; a second call would toast an
    // "expired" error even though the first succeeded. Track the token
    // we've already attempted in the current tab.
    const attemptedKey = "eviq._handoff_attempted";
    try {
      if (sessionStorage.getItem(attemptedKey) === token) return;
      sessionStorage.setItem(attemptedKey, token);
    } catch (_e) {}

    const API = process.env.REACT_APP_BACKEND_URL || "";
    (async () => {
      try {
        const r = await fetch(`${API}/api/continuity/consume-handoff`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token }),
        });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const j = await r.json();
        try {
          localStorage.setItem("eviq.owner_mode", "1");
          if (j.owner_name) {
            localStorage.setItem("eviq.tesla_user_name", j.owner_name);
          }
        } catch (_e) {}
        window.dispatchEvent(new CustomEvent("eviq:subscription-changed"));
        toast.success("Owner mode enabled", {
          description: j.owner_name
            ? `Welcome back, ${j.owner_name.split(" ")[0]}.`
            : "You're signed in as the account owner.",
        });
      } catch (_e) {
        toast.error("Handoff failed", {
          description: "The QR code may have expired. Generate a new one on the desktop admin.",
        });
      }
    })();
  }, []);

  useEffect(() => {
    // Owner reconciliation — fires on EVERY mount of /app.
    //
    // iOS PWAs installed to the home screen run in an isolated storage
    // sandbox that does NOT share localStorage with Safari. So the same
    // Tesla-connected user can appear "not an owner" in the PWA even after
    // signing in via Safari.
    //
    // Fix: whenever there's a Tesla session_id in *this* context, hit
    // /api/tesla/status and let the backend authoritatively say
    // is_owner=true/false based on OWNER_EMAILS. That's the same signal we
    // use elsewhere — but now guaranteed to run in PWA context too.
    const API = process.env.REACT_APP_BACKEND_URL || "";
    let cancelled = false;
    (async () => {
      let sid = null;
      try {
        sid = localStorage.getItem("eviq.tesla_session_id");
      } catch (_e) {}
      if (!sid) return;
      try {
        const r = await fetch(`${API}/api/tesla/status?session_id=${encodeURIComponent(sid)}`);
        if (!r.ok) return;
        const data = await r.json();
        if (cancelled || !data.connected) return;
        if (data.is_owner) {
          try {
            localStorage.setItem("eviq.owner_mode", "1");
            if (data.user_name) {
              localStorage.setItem("eviq.tesla_user_name", data.user_name);
            }
          } catch (_e) {}
          window.dispatchEvent(new CustomEvent("eviq:subscription-changed"));
        }
        // Founding members: lifetime access follows their Tesla sign-in on
        // any device — no localStorage flag or activation URL required.
        if (data.user_email) {
          const member = activateFoundingByEmail(data.user_email);
          if (member) {
            try {
              localStorage.removeItem("eviq.preview_started_at");
            } catch (_e) {}
            window.dispatchEvent(new CustomEvent("eviq:subscription-changed"));
          }
        }
      } catch (_e) {
        // Non-fatal — user can always unlock via More → Owner unlock.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const check = () => setIsDesktop(window.innerWidth >= 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  useEffect(() => {
    pingSync();
  }, [tab]);

  // ⌘K / Ctrl+K opens the DEMO vehicle picker — but never while a live
  // OAuth session is connected (the connected car is the only vehicle).
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        let hasLiveSession = false;
        try { hasLiveSession = !!localStorage.getItem("eviq.tesla_session_id"); } catch { /* noop */ }
        if (!hasLiveSession) setPickerOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // If the user taps the More tab, always land on the hub — never a stale sub-view.
  const handleSetTab = (nextTab) => {
    if (nextTab === "care") setMoreSub(null);
    if (nextTab === "blog") {
      if (latestPostRef.current) {
        try { window.localStorage.setItem(BLOG_LAST_READ_KEY, latestPostRef.current); } catch { /* noop */ }
      }
      setBlogNew(false);
    }
    setTab(nextTab);
  };

  return (
    <div
      data-testid="mobile-app-page"
      className="min-h-screen w-full bg-black text-white flex flex-col md:items-center md:justify-center overflow-hidden relative"
      style={{
        backgroundImage:
          "radial-gradient(ellipse 60% 40% at 50% 0%, rgba(0,229,255,0.08), transparent 70%), radial-gradient(ellipse 40% 30% at 50% 100%, rgba(57,255,20,0.05), transparent 60%)",
      }}
    >
      {isDesktop && <DesktopChrome tab={tab} />}

      {/* Builderfest vote nudge — first-time visitors only, gone forever
          once this device dismisses it or this IP has voted. */}
      <VoteNudge offsetClass="bottom-24" />

      <div
        className={
          isDesktop
            ? "relative rounded-[48px] bg-[#0d0d0d] border-2 border-white/15 shadow-[0_40px_120px_-20px_rgba(0,229,255,0.35)]"
            : "relative w-full min-h-screen bg-black"
        }
        style={
          isDesktop
            ? { padding: "12px 10px", width: "400px", zIndex: 10 }
            : {}
        }
        data-testid="mobile-app-shell"
      >
        {isDesktop && (
          <div className="absolute top-2 left-1/2 -translate-x-1/2 w-32 h-6 bg-black rounded-full z-20" />
        )}

        <div
          className={
            isDesktop
              ? "relative bg-black rounded-[38px] overflow-hidden flex flex-col"
              : "relative flex flex-col overflow-hidden"
          }
          style={
            isDesktop
              ? { width: "380px", height: "780px" }
              : {
                  // Fill the visible viewport EXACTLY — not the document.
                  // 100dvh accounts for iOS Safari's dynamic browser chrome
                  // and (crucially) inside an installed PWA it collapses to
                  // the full standalone viewport. min-h-screen was letting
                  // the page grow taller than the viewport, which pushed
                  // the bottom nav off-screen in the PWA context.
                  height: "100dvh",
                  minHeight: "-webkit-fill-available",
                }
          }
        >
          <StatusBar />
          <OwnerModeBanner />
          <FoundingMemberBanner />
          <MobileAppInstallBanner />
          <HomePriceOnboardingHost />
          <AppHeader
            stateCode={stateCode}
            onChangeState={setStateCode}
          />
          <PreviewModeBanner />

          <main
            className="flex-1 overflow-y-auto px-4 pb-28"
            data-testid="mobile-app-content"
          >
            {tab === "home" && <HomeView stateCode={stateCode} />}
            {tab === "dna" && (
              <LiveGate tab="dna">
                <BatteryDNAView stateCode={stateCode} />
              </LiveGate>
            )}
            {tab === "ai" && (
              <LiveGate tab="ai">
                <AIView stateCode={stateCode} />
              </LiveGate>
            )}
            {tab === "predict" && (
              <LiveGate tab="predict">
                <PredictView stateCode={stateCode} />
              </LiveGate>
            )}
            {tab === "care" && (
              <CareView sub={moreSub} setSub={setMoreSub} stateCode={stateCode} />
            )}
            {tab === "blog" && <BlogView />}

            {/* Escape hatch: lets a mobile visitor pop out of the app-shell
                back to the marketing site if they hit /app by accident. */}
            <div className="mt-8 pb-4 flex justify-center">
              <button
                type="button"
                data-testid="view-full-site-link"
                onClick={() => {
                  try {
                    window.localStorage.setItem("eviq_view_pref", "desktop");
                  } catch (_e) {}
                  window.location.href = "/?desktop=1";
                }}
                className="text-[11px] font-mono uppercase tracking-[0.18em] text-zinc-500 hover:text-white transition-colors"
              >
                View full website →
              </button>
            </div>
          </main>

          <TabBar tab={tab} setTab={handleSetTab} blogNew={blogNew} />

          {pickerOpen && (
            <VehiclePickerSheet onClose={() => setPickerOpen(false)} />
          )}

          {showExpiredLock && <PreviewExpiredLock />}
        </div>
      </div>
    </div>
  );
}

// ---------- Desktop marketing chrome (kept minimal) ----------
const DesktopChrome = ({ tab }) => {
  const labelMap = {
    home: "Dashboard",
    ai: "AI Narrative",
    range: "Range Confidence",
    trip: "Trip Planner",
    more: "All Tools",
  };
  return (
    <>
      <Link
        to="/"
        data-testid="mobile-app-back-to-web"
        className="absolute top-6 left-6 z-20 flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors font-medium"
      >
        <ChevronLeft className="w-4 h-4" strokeWidth={2.5} />
        Back to website
      </Link>
      <div className="absolute top-6 right-6 z-20 flex items-center gap-3">
        <Logo size={22} />
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
          EVIQ · Mobile
        </span>
      </div>
      <div className="absolute left-8 lg:left-16 top-1/2 -translate-y-1/2 hidden lg:block max-w-[280px]">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-3">
          [ In-app preview ]
        </div>
        <div className="font-display text-3xl text-white font-semibold leading-tight mb-4">
          Your battery,{" "}
          <span className="brand-text-gradient">in your pocket.</span>
        </div>
        <p className="text-zinc-400 text-sm leading-relaxed">
          Same intelligence engine as the website. Tap the tab bar to switch tools —
          all views react to your selected vehicle.
        </p>
      </div>
      <div className="absolute right-8 lg:right-16 top-1/2 -translate-y-1/2 hidden lg:block max-w-[240px] text-right">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-3">
          [ Current view ]
        </div>
        <div className="font-display text-2xl text-white font-semibold">
          {labelMap[tab]}
        </div>
      </div>
    </>
  );
};

// ---------- Status + header ----------
// Live device status bar — real values where the web platform allows:
// • Time: real device clock, ticks every 15s.
// • Battery: Battery Status API (Android/Chrome). iOS Safari doesn't expose
//   it — icon stays generic there.
// • Connectivity: online/offline is real everywhere; cellular "bars" come
//   from the Network Information API's effective speed where available.
const StatusBar = () => {
  const [now, setNow] = useState(() => new Date());
  const [battery, setBattery] = useState(null); // { level, charging }
  const [online, setOnline] = useState(() => (typeof navigator !== "undefined" ? navigator.onLine : true));
  const [netType, setNetType] = useState(() => navigator.connection?.effectiveType || null);

  useEffect(() => {
    const clock = setInterval(() => setNow(new Date()), 15_000);
    const onNet = () => setOnline(navigator.onLine);
    window.addEventListener("online", onNet);
    window.addEventListener("offline", onNet);
    const conn = navigator.connection;
    const onConn = () => setNetType(conn?.effectiveType || null);
    conn?.addEventListener?.("change", onConn);
    let batt;
    if (navigator.getBattery) {
      navigator.getBattery().then((b) => {
        batt = b;
        const sync = () => setBattery({ level: Math.round(b.level * 100), charging: b.charging });
        sync();
        b.addEventListener("levelchange", sync);
        b.addEventListener("chargingchange", sync);
      }).catch(() => {});
    }
    return () => {
      clearInterval(clock);
      window.removeEventListener("online", onNet);
      window.removeEventListener("offline", onNet);
      conn?.removeEventListener?.("change", onConn);
    };
  }, []);

  const time = now.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  const signalOpacity = !online ? 0.25 : netType === "slow-2g" || netType === "2g" ? 0.45 : netType === "3g" ? 0.7 : 1;

  return (
    <div className="flex items-center justify-between px-6 pt-3 pb-2 text-[11px] font-mono text-white/90 shrink-0" data-testid="device-status-bar">
      <span data-testid="status-bar-time">{time}</span>
      <div className="flex items-center gap-1.5">
        <Signal className="w-3 h-3" strokeWidth={2.5} style={{ opacity: signalOpacity }} />
        <Wifi className="w-3 h-3" strokeWidth={2.5} style={{ opacity: online ? 1 : 0.25 }} />
        {battery ? (
          <span className="inline-flex items-center gap-0.5" data-testid="status-bar-battery">
            <span className="text-[9px]" style={{ color: battery.charging ? "#39FF14" : "rgba(255,255,255,0.9)" }}>
              {battery.level}%
            </span>
            <BatteryFull className="w-4 h-4" strokeWidth={2} style={{ opacity: 0.3 + 0.7 * (battery.level / 100), color: battery.charging ? "#39FF14" : undefined }} />
          </span>
        ) : (
          <BatteryFull className="w-4 h-4" strokeWidth={2} />
        )}
      </div>
    </div>
  );
};

// Host: renders the Home $/kWh onboarding modal once per live vehicle.
const HomePriceOnboardingHost = () => {
  const { vehicle } = useVehicle();
  if (!vehicle?.live || !vehicle?.vin) return null;
  return <HomePriceOnboarding vehicle={vehicle} />;
};


const AppHeader = ({ stateCode, onChangeState }) => {
  const { vehicle, live } = useVehicle();

  // When Tesla is streaming live data with GPS coords, auto-set the state
  // context from the vehicle's actual location so the climate/weather
  // models downstream use the right region without any user tap.
  useEffect(() => {
    if (!vehicle?.live) return;
    const lat = vehicle.location?.lat;
    const lng = vehicle.location?.lng;
    const guessed = stateFromCoords(lat, lng);
    if (guessed && guessed !== stateCode) {
      onChangeState(guessed);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicle?.location?.lat, vehicle?.location?.lng]);

  const isLive = !!vehicle?.live;
  const brandLabel = vehicle?.brand === "ford" ? "Ford"
    : vehicle?.brand === "volvo" ? "Volvo" : "Tesla";
  // Session-level "connected" flag — an OAuth session exists even if the
  // Fleet API hasn't returned vehicle data yet. Used to keep the refresh
  // button visible from the moment sign-in completes so the user always
  // has a way to trigger a fresh Tesla fetch.
  const isSessionConnected = !!live?.connected;
  // Live mode: build sub-line strictly from Tesla profile fields — never
  // the mock odometer. We show display_name + VIN suffix (or odometer
  // when we've received a live vehicle_data payload). The exterior color
  // is intentionally omitted per user request — it's noise, not signal.
  const subLine = isLive
    ? (() => {
        const parts = [];
        // Don't repeat the header name (some OEMs, e.g. Ford, return a
        // display_name identical to "<year> <model>" already shown above).
        const headerName = `${vehicle.year || ""} ${vehicle.short || vehicle.model || ""}`.trim().toLowerCase();
        if (vehicle.displayName && vehicle.displayName.trim().toLowerCase() !== headerName) {
          parts.push(vehicle.displayName);
        }
        // Only include odometer if we truly got it from vehicle_data
        // (liveAgeSec > 0 means we saw a live payload).
        if ((vehicle.liveAgeSec ?? 0) > 0 && typeof vehicle.odometer === "number") {
          parts.push(`${vehicle.odometer.toLocaleString()} mi`);
        } else if (vehicle.vin) {
          parts.push(`VIN ····${vehicle.vin.slice(-4)}`);
        }
        return parts.filter(Boolean).join(" · ");
      })()
    : `${(vehicle.odometer ?? 0).toLocaleString()} mi`;

  return (
    <div className="shrink-0 border-b border-white/5">
      {/* Row 1 — vehicle + language. Non-interactive: values come from the
          live Tesla OAuth session (when connected) or the currently
          selected demo vehicle otherwise. No dropdown. */}
      <div className="px-4 pt-2 pb-2 flex items-center justify-between gap-3">
        <div
          data-testid="mobile-app-vehicle-display"
          className="flex items-center gap-2.5 min-w-0 flex-1"
        >
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center border border-white/10 shrink-0"
            style={{ background: "rgba(57,255,20,0.08)" }}
          >
            <BatteryCharging className="w-4 h-4" style={{ color: "#39FF14" }} strokeWidth={2} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm text-white font-semibold leading-tight flex items-center gap-1 min-w-0">
              {isLive ? (
                <>
                  <span data-testid="mobile-app-vehicle-name" className="truncate min-w-0">
                    {vehicle.year} {vehicle.short || vehicle.model}
                  </span>
                  <span
                    data-testid="mobile-app-live-badge"
                    className="ml-1 shrink-0 inline-flex items-center gap-1 text-[8px] font-mono uppercase tracking-[0.14em] rounded-full px-1.5 py-0.5 border border-[#39FF14]/50 text-[#39FF14] bg-[#39FF14]/[0.10]"
                    title={`Live from ${brandLabel} · ${vehicle.liveAgeSec ?? 0}s ago`}
                  >
                    <span className="w-1 h-1 rounded-full bg-[#39FF14] animate-pulse" aria-hidden="true" />
                    Live
                  </span>
                </>
              ) : isSessionConnected ? (
                <span className="text-zinc-400 font-mono text-[11px] tracking-wider uppercase">
                  Connecting to {brandLabel}…
                </span>
              ) : (
                <span className="text-zinc-500 font-mono text-[11px] tracking-wider uppercase">
                  Sign in to connect
                </span>
              )}
            </div>
            <div data-testid="mobile-app-vehicle-subline" className="font-mono text-[10px] text-zinc-500 truncate">
              {isLive ? subLine || "—" : isSessionConnected ? "Fetching vehicle profile…" : "Vehicle info appears here after sign-in"}
            </div>
          </div>
        </div>

        {isSessionConnected && (
          <button
            type="button"
            onClick={() => live.refresh()}
            disabled={live.loading}
            data-testid="mobile-app-live-refresh"
            aria-label="Refresh live Tesla data"
            className={`shrink-0 w-8 h-8 rounded-lg border transition-colors flex items-center justify-center disabled:opacity-40 ${
              live.wakingUp
                ? "border-[#39FF14]/60 bg-[#39FF14]/[0.14] shadow-[0_0_10px_rgba(57,255,20,0.35)] animate-pulse"
                : "border-[#39FF14]/25 bg-[#39FF14]/[0.06] hover:bg-[#39FF14]/[0.14]"
            }`}
            title={
              live.wakingUp
                ? "Waking your vehicle and fetching live data…"
                : "Refresh live data (bypasses 60s cache)"
            }
          >
            <RefreshCw
              className={`w-3.5 h-3.5 ${live.loading || live.wakingUp ? "animate-spin" : ""}`}
              style={{ color: "#39FF14" }}
              strokeWidth={2.4}
            />
          </button>
        )}

        <div className="shrink-0 flex items-center gap-1.5">
          <FounderChip />
          <LanguageSwitcher compact />
        </div>
      </div>

      {/* Row 2 — location. Hidden entirely until Tesla returns real GPS
          coords, so users don't see a placeholder California/CA state
          before their car is actually located. */}
      {isLive && vehicle.location?.lat == null && (vehicle.liveAgeSec ?? -1) >= 0 && live?.data?.location?.permission_missing && (
        <div className="px-4 pb-2 flex items-center gap-2 border-t border-white/[0.04] pt-2" data-testid="mobile-app-location-permission-hint">
          <MapPin className="w-3 h-3 shrink-0" style={{ color: "#FFB020" }} strokeWidth={2} />
          <span className="text-[10px] leading-tight" style={{ color: "#FFB020" }}>
            Location permission not granted — disconnect and sign in with {brandLabel} again,
            keeping <span className="font-semibold">Location</span> checked, to unlock
            climate-accurate insights.
          </span>
        </div>
      )}
      {isLive && vehicle.location?.lat != null && vehicle.location?.lng != null && (
        <div className="px-4 pb-2 flex items-center gap-2 border-t border-white/[0.04] pt-2">
          <MapPin className="w-3 h-3 text-zinc-500 shrink-0" strokeWidth={2} />
          <span className="font-mono text-[9px] uppercase tracking-wider text-zinc-500 shrink-0">
            Location
          </span>
          <div
            data-testid="mobile-app-location-display"
            className="flex-1 text-[11px] text-white truncate flex items-center gap-1.5"
          >
            {(() => {
              // Derive the shown state straight from GPS — never render the
              // default/last-picked state beside real coords (a FL car must
              // not read "California" because the guess didn't resolve).
              const gpsState = stateFromCoords(vehicle.location.lat, vehicle.location.lng);
              const gpsName = gpsState
                ? US_STATES.find((s) => s.code === gpsState)?.name || gpsState
                : null;
              return gpsState ? (
                <>
                  <span className="truncate">
                    {gpsState} · {gpsName}
                  </span>
                  <span className="text-zinc-500">·</span>
                </>
              ) : null;
            })()}
            <span className="font-mono text-zinc-400 text-[10px]">
              {vehicle.location.lat.toFixed(2)}, {vehicle.location.lng.toFixed(2)}
            </span>
          </div>
          <UpdatedStamp ts={live?.lastUpdated} stale={live?.data?.stale} />
        </div>
      )}
      {isLive && vehicle.location?.lat == null && live?.lastUpdated && (
        <div className="px-4 pb-2 flex justify-end border-t border-white/[0.04] pt-1.5">
          <UpdatedStamp ts={live.lastUpdated} stale={live?.data?.stale} />
        </div>
      )}
    </div>
  );
};

// Ticking freshness stamp — "updated 12s ago" so owners know exactly how
// fresh the telemetry on screen is.
const UpdatedStamp = ({ ts, stale }) => {
  const [, tick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => tick((n) => n + 1), 5000);
    return () => clearInterval(id);
  }, []);
  if (!ts) return null;
  const s = Math.max(0, Math.round((Date.now() - ts) / 1000));
  const label = s < 60 ? `${s}s ago` : s < 3600 ? `${Math.floor(s / 60)}m ago` : `${Math.floor(s / 3600)}h ago`;
  return (
    <span
      className="shrink-0 font-mono text-[9px] uppercase tracking-wider"
      style={{ color: stale ? "#FFB020" : "rgba(255,255,255,0.35)" }}
      data-testid="mobile-app-updated-stamp"
    >
      {stale ? `cached · ${label}` : `updated ${label}`}
    </span>
  );
};

// ---------- Preview-mode banner ----------
// Shown to any visitor who has not yet subscribed. Everything in /app is
// browsable as a preview for 7 days from the visitor's first landing;
// after that the app hard-gates until the visitor picks a plan.
const PreviewModeBanner = () => {
  const { paid, preview, tier } = useSubscription();

  // No banner for anyone who already has a plan — paid subscribers
  // (basic / yearly / lifetime), founding members, or the signed-in owner.
  if (paid || (tier && tier !== "preview")) return null;

  // Expired: hard red banner with a strong upgrade CTA. The rest of the
  // page continues to render behind it, but action buttons across /app
  // will already have been re-gated by the underlying tier logic.
  if (preview.expired) {
    return (
      <div
        data-testid="preview-expired-banner"
        className="shrink-0 border-b border-[#FF6B57]/40 bg-[#FF6B57]/[0.08] px-4 py-2 flex items-center gap-3"
      >
        <div
          className="w-6 h-6 rounded-full flex items-center justify-center shrink-0"
          style={{ background: "rgba(255,107,87,0.14)", border: "1px solid rgba(255,107,87,0.5)" }}
        >
          <Lock className="w-3 h-3" style={{ color: "#FF6B57" }} strokeWidth={2.5} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[11px] font-semibold text-white leading-tight">
            Preview expired
          </div>
          <div className="text-[10px] text-zinc-300 leading-tight font-mono">
            Choose a plan to keep using EVIQ — from just $9.99/mo.
          </div>
        </div>
        <button
          onClick={() => openSubscribeGate("preview-expired-banner")}
          data-testid="preview-expired-cta"
          className="shrink-0 text-[10px] font-semibold uppercase tracking-wider brand-gradient text-black rounded-full px-3 py-1.5 hover:opacity-90 transition-opacity"
        >
          Choose plan
        </button>
      </div>
    );
  }

  // Active preview: quiet amber strip with a countdown.
  const daysLeft = Math.max(0, Math.ceil(preview.daysRemaining));
  const dayLabel = daysLeft === 1 ? "1 day" : `${daysLeft} days`;
  return (
    <div
      data-testid="preview-mode-banner"
      className="shrink-0 border-b border-[#FFB020]/25 bg-[#FFB020]/[0.06] px-4 py-2 flex items-center gap-3"
    >
      <div
        className="w-6 h-6 rounded-full flex items-center justify-center shrink-0"
        style={{ background: "rgba(255,176,32,0.12)", border: "1px solid rgba(255,176,32,0.4)" }}
      >
        <Lock className="w-3 h-3" style={{ color: "#FFB020" }} strokeWidth={2.5} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[11px] font-semibold text-white leading-tight">
          Free preview · <span data-testid="preview-days-remaining">{dayLabel}</span> left
        </div>
        <div className="text-[10px] text-zinc-400 leading-tight font-mono">
          Full features unlocked. Then pick a plan to continue.
        </div>
      </div>
      <button
        onClick={() => openSubscribeGate("preview-banner")}
        data-testid="preview-banner-cta"
        className="shrink-0 text-[10px] font-semibold uppercase tracking-wider brand-gradient text-black rounded-full px-3 py-1.5 hover:opacity-90 transition-opacity"
      >
        Choose plan
      </button>
    </div>
  );
};

// ---------- Tab bar ----------
const TabBar = ({ tab, setTab, blogNew }) => {
  const { t } = useTranslation();
  return (
    <nav
      data-testid="mobile-app-tabbar"
      className="absolute bottom-0 left-0 right-0 bg-black/95 backdrop-blur-xl border-t border-white/10 px-2 pt-2 z-30"
      style={{
        // iOS home-indicator safe area — the extra padding keeps the tab
        // labels from clipping under the pill on installed PWAs.
        paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 1rem)",
      }}
    >
      <div className="flex items-center justify-around">
        {TAB_DEFS.map((tDef) => {
          const Icon = tDef.icon;
          const active = tab === tDef.id;
          return (
            <button
              key={tDef.id}
              onClick={() => setTab(tDef.id)}
              data-testid={`mobile-app-tab-${tDef.id}`}
              aria-label={t(tDef.tKey, tDef.fallback)}
              aria-current={active ? "page" : undefined}
              className="flex flex-col items-center gap-1 py-1.5 px-2 rounded-md transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-[#39FF14]/70 focus-visible:ring-offset-1 focus-visible:ring-offset-black"
            >
              <span className="relative">
                <Icon
                  className="w-5 h-5"
                  strokeWidth={active ? 2.5 : 2}
                  style={{ color: active ? "#39FF14" : "#71717A" }}
                />
                {tDef.id === "blog" && blogNew && (
                  <span
                    data-testid="blog-new-badge"
                    className="absolute -top-0.5 -right-1 w-2 h-2 rounded-full bg-[#39FF14]"
                    style={{ boxShadow: "0 0 6px rgba(57,255,20,0.8)" }}
                  />
                )}
              </span>
              <span
                className="text-[10px] font-medium"
                style={{ color: active ? "#39FF14" : "#71717A" }}
              >
                {t(tDef.tKey, tDef.fallback)}
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
};

// ---------- Sheets ----------
const Sheet = ({ title, onClose, children, testid }) => (
  <div
    className="absolute inset-0 z-30 flex items-end bg-black/60 backdrop-blur-sm"
    data-testid={testid}
    onClick={onClose}
  >
    <div
      className="w-full bg-[#0a0a0a] border-t border-white/10 rounded-t-2xl max-h-[85%] overflow-hidden flex flex-col"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
        <span className="text-sm text-white font-semibold">{title}</span>
        <button
          onClick={onClose}
          className="w-7 h-7 rounded-full flex items-center justify-center hover:bg-white/5"
          aria-label="Close"
        >
          <X className="w-4 h-4 text-zinc-400" strokeWidth={2} />
        </button>
      </div>
      <div className="overflow-y-auto p-2">{children}</div>
    </div>
  </div>
);

const VehiclePickerSheet = ({ onClose }) => {
  const { vehicles, vehicleId, setVehicleId } = useVehicle();
  const currentModelKey = (vehicles.find((v) => v.id === vehicleId) || {}).modelKey;
  const [modelKey, setModelKey] = useState(currentModelKey || MODEL_LIST[0].modelKey);

  const modelYears = useMemo(
    () => vehicles.filter((v) => v.modelKey === modelKey),
    [vehicles, modelKey]
  );
  const modelsInCatalog = useMemo(() => {
    const seen = new Set();
    const result = [];
    vehicles.forEach((v) => {
      if (!v.modelKey || seen.has(v.modelKey)) return;
      seen.add(v.modelKey);
      result.push({
        modelKey: v.modelKey,
        model: MODEL_LIST.find((m) => m.modelKey === v.modelKey)?.model || v.name,
        short: v.short,
      });
    });
    return result;
  }, [vehicles]);

  return (
    <Sheet title="Choose vehicle" onClose={onClose} testid="mobile-app-vehicle-sheet">
      <div className="mb-3">
        <div className="font-mono text-[9px] uppercase tracking-wider text-zinc-500 mb-1.5 px-1">
          Model
        </div>
        <div className="flex flex-wrap gap-1.5">
          {modelsInCatalog.map((m) => {
            const active = m.modelKey === modelKey;
            return (
              <button
                key={m.modelKey}
                onClick={() => setModelKey(m.modelKey)}
                data-testid={`mobile-app-model-tab-${m.modelKey}`}
                className={`px-3 py-1.5 rounded-full border text-[11px] font-medium transition-colors ${
                  active
                    ? "border-[#39FF14]/40 bg-[#39FF14]/[0.08] text-white"
                    : "border-white/10 bg-white/[0.02] text-zinc-400 hover:text-white"
                }`}
              >
                {m.model}
              </button>
            );
          })}
        </div>
      </div>

      <div className="font-mono text-[9px] uppercase tracking-wider text-zinc-500 mb-1.5 px-1">
        Year · {modelYears.length} option{modelYears.length !== 1 ? "s" : ""}
      </div>
      <div className="space-y-1.5 max-h-[420px] overflow-y-auto">
        {modelYears.map((v) => {
          const active = v.id === vehicleId;
          return (
            <button
              key={v.id}
              onClick={() => {
                setVehicleId(v.id);
                onClose();
              }}
              data-testid={`mobile-app-vehicle-option-${v.id}`}
              className={`w-full text-left rounded-xl border px-3 py-2.5 transition-colors flex items-center gap-3 ${
                active
                  ? "border-[#39FF14]/40 bg-[#39FF14]/[0.06]"
                  : "border-white/10 bg-white/[0.02] hover:bg-white/[0.04]"
              }`}
            >
              <div className="min-w-0 flex-1">
                <div className="text-sm text-white font-semibold truncate flex items-center gap-1.5">
                  {v.year} {v.short}
                  {v.isPreview && (
                    <span className="font-mono text-[8px] uppercase px-1 rounded text-[#00E5FF] bg-[#00E5FF]/10 border border-[#00E5FF]/30">
                      Preview
                    </span>
                  )}
                </div>
                <div className="font-mono text-[10px] text-zinc-500 truncate">
                  SoH {v.sohCurrent}% · {v.odometer.toLocaleString()} mi
                </div>
              </div>
              {active && (
                <Check className="w-4 h-4 shrink-0" style={{ color: "#39FF14" }} strokeWidth={2.5} />
              )}
            </button>
          );
        })}
      </div>
    </Sheet>
  );
};
